import { EventInterface, EventType } from '@/types/event'
import { SignUpRecord } from './types/signUpRecord'
import { Country } from './types/country'
import { LocalDate } from './types/localDate'
import { Office } from './types/office'
import { IpGeo } from './types/ipGeo'

/**
 * Decode a Directus json column, which comes back JSON-encoded as a string.
 */
function parseJsonColumn<T>(value: unknown, fallback: T): T {
  if (typeof value !== 'string') return (value as T) ?? fallback

  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

/**
 * Map a saved `sign_up_record` row back to the camelCase shape the app uses.
 *
 * `uuidSignUpRecord` is the row's primary key: the save/subscribe endpoints
 * upsert on it, so passing it back on the follow-up subscribe call is what
 * keeps a visitor's two submissions on a single record instead of duplicating.
 */
function toSignUpRecord(record: Record<string, any>): SignUpRecord {
  return {
    ...record,
    uuidSignUpRecord: record.id,
    givenName: record.given_name ?? null,
    familyName: record.family_name ?? null,
    socialMedia: parseJsonColumn<string[]>(record.social_media, []),
    data: parseJsonColumn<Record<string, any>>(record.data, {}),
    createdAt: record.date_created,
    updatedAt: record.date_updated,
  } as SignUpRecord
}

export class Api {
  private contactsUrl: string
  private contactsToken: string
  private creationComUrl: string | null
  private creationComToken: string | null

  constructor() {
    // Contacts Directus (signup records, subscriptions) — the primary write path.
    const contactsUrl = import.meta.env.VITE_CONTACTS_API_URL
    const contactsToken = import.meta.env.VITE_CONTACTS_API_TOKEN

    if (!contactsUrl) throw new Error('VITE_CONTACTS_API_URL is not set')
    if (!contactsToken) throw new Error('VITE_CONTACTS_API_TOKEN is not set')

    this.contactsUrl = contactsUrl
    this.contactsToken = contactsToken

    // Creation.com Directus (geo data: countries, offices; event listings)
    this.creationComUrl = import.meta.env.VITE_CREATION_COM_API_URL || null
    this.creationComToken = import.meta.env.VITE_CREATION_COM_API_TOKEN || null
  }

  /**
   * Make a request to the contacts Directus API.
   */
  private async contactsRequest(method: string, path: string, body?: any): Promise<any> {
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.contactsToken}`,
      },
    }
    if (body != null) {
      options.body = JSON.stringify(body)
    }

    const response = await fetch(`${this.contactsUrl}${path}`, options)
    const payload = await response.json().catch(() => null)

    if (!response.ok) {
      // Directus errors carry the useful detail; the status text alone is noise
      // on the INFObytes page, which surfaces this message to the visitor.
      throw new Error(
        payload?.errors?.[0]?.message || `Contacts API ${response.status}: ${response.statusText}`
      )
    }

    return payload
  }

  private contactsGet(path: string): Promise<any> {
    return this.contactsRequest('GET', path)
  }

  private contactsPost(path: string, body: any): Promise<any> {
    return this.contactsRequest('POST', path, body)
  }

  private contactsPatch(path: string, body: any): Promise<any> {
    return this.contactsRequest('PATCH', path, body)
  }

  /**
   * Fetch events directly from creation-com Directus (client-side), mirroring
   * `fetchCountries`. Replaces the legacy `/api/events` Cloudflare Pages Function,
   * whose server-side (edge) request to the Directus backend failed in production.
   */
  private async fetchEventsFromDirectus(
    office: Office,
    localDate: LocalDate
  ): Promise<EventInterface[]> {
    if (!this.creationComUrl || !this.creationComToken) return []

    const [year, month, day] = localDate.split('-').map(Number)
    const endOfDay = new Date()
    endOfDay.setFullYear(year, month - 1, day)
    endOfDay.setHours(23, 59, 59, 999)

    const weekBeforeDate = new Date(endOfDay.getTime())
    weekBeforeDate.setDate(weekBeforeDate.getDate() - (office === 'ca' ? 180 : 7))

    // `time_start` is a timezone-naive Directus `dateTime` field storing each
    // event's literal local (venue) wall-clock time. Comparing it against
    // `.toISOString()` (which converts to UTC) shifts the day boundary by the
    // runtime's UTC offset, silently excluding events later in the day for
    // any timezone ahead of UTC. Format as a naive local string instead so
    // the comparison lines up with how the value is stored.
    const pad = (n: number) => String(n).padStart(2, '0')
    const toNaiveIso = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
        d.getMinutes()
      )}:${pad(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`

    const params = new URLSearchParams()
    params.set(
      'fields',
      'id,office,time_start,location_name,content.related_content.to.person.title,content.related_content.to.person.photo'
    )
    params.set(
      'filter',
      JSON.stringify({
        _and: [
          { type: { _eq: 'event' } },
          { office: { _eq: office } },
          { time_start: { _gte: toNaiveIso(weekBeforeDate) } },
          { time_start: { _lte: toNaiveIso(endOfDay) } },
        ],
      })
    )
    params.set('limit', '-1')

    const response = await fetch(`${this.creationComUrl}/items/event?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${this.creationComToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(`creation.com Directus ${response.status}: ${response.statusText}`)
    }

    const { data } = await response.json()

    return (data || []).map((event: any): EventInterface => {
      const person = event.content?.related_content?.[0]?.to?.person?.[0]
      return {
        id: event.id,
        office: event.office,
        date: event.time_start,
        location: event.location_name,
        type: EventType.Scheduled,
        ...(person
          ? { speaker: person.title, speakerImage: person.photo }
          : { speaker: undefined, speakerImage: undefined }),
      }
    })
  }

  async fetchEvents(office: Office, localDate: LocalDate): Promise<EventInterface[]> {
    const events = await this.fetchEventsFromDirectus(office, localDate)

    // Dual-write: upsert fetched events into contacts Directus (fire-and-forget)
    if (events?.length) {
      for (const event of events) {
        if (!event.id) continue
        const body = {
          event_id: event.id,
          type: event.type || 'scheduled',
          office: event.office ?? office,
          location: event.location,
          speaker: event.speaker ?? null,
          date: event.date,
          time: event.time ?? null,
        }
        this.contactsGet(
          `/items/event?filter[event_id][_eq]=${encodeURIComponent(event.id)}&limit=1`
        )
          .then((res: any) => {
            const existing = res?.data
            if (existing?.length) {
              return this.contactsPatch(`/items/event/${existing[0].id}`, body)
            } else {
              return this.contactsPost('/items/event', body)
            }
          })
          .catch((err: any) => console.warn('[dual-write:events]', err.message))
      }
    }

    return events
  }

  async saveRecord(signUpRecord: Partial<SignUpRecord>): Promise<SignUpRecord> {
    const response = await this.contactsPost('/items/sign_up_record/save', { signUpRecord })

    return toSignUpRecord(response.data.record)
  }

  async subscribeRecordToInfobytes(signUpRecord: Partial<SignUpRecord>): Promise<SignUpRecord> {
    const response = await this.contactsPost('/items/sign_up_record/subscribe', {
      signUpRecord,
      lists: ['infobytes'],
    })

    return toSignUpRecord(response.data.record)
  }

  async lookupIp(): Promise<{ country_code: string; geo?: IpGeo | null }> {
    const request = await fetch(`/api/ip`)
    return await request.json()
  }

  /**
   * Fetch countries from creation-com Directus, the source of truth for geo data.
   *
   * Throws when Directus isn't configured rather than returning an empty set, so
   * the store keeps the `fallbackCountries` it was seeded with (it only assigns
   * `countries` on success) instead of losing the CMI office countries.
   */
  async fetchCountries(): Promise<Record<string, Country>> {
    if (!this.creationComUrl || !this.creationComToken) {
      throw new Error('VITE_CREATION_COM_API_URL / _TOKEN are not set')
    }

    const response = await fetch(
      `${this.creationComUrl}/items/country?fields=id,name,office.id&limit=-1`,
      {
        headers: {
          Authorization: `Bearer ${this.creationComToken}`,
        },
      }
    )

    const { data } = await response.json()

    const countries: Record<string, Country> = {}
    for (const item of data) {
      countries[item.id] = {
        name: item.name,
        officeCode: item.office?.id || 'au',
        code: item.id,
      }
    }

    return countries
  }
}

export const api = new Api()
